import { memo, useEffect, useRef, useState } from 'react';
import { HERO_YOUTUBE_URL } from '../constants';
import { useLanguage } from '../LanguageContext';
import { api } from '../api/client';
import { getYoutubeEmbedUrl, getYoutubeVideoId } from '../utils/youtube';
import { readCachedHeroVideoId, writeCachedHeroVideoId, clearCachedHeroVideoId } from '../utils/heroVideoCache';

function HeroArtwork() {
  const logoSrc = `${process.env.PUBLIC_URL}/logo-maalin-iyo-habeen.png`;

  return (
    <div className="hero__visual" aria-hidden="true">
      <div className="hero__visual-bg" aria-hidden="true">
        <svg
          className="hero__visual-ornament"
          viewBox="0 0 400 360"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="hv-sun" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f58220" stopOpacity="1" />
              <stop offset="100%" stopColor="#f58220" stopOpacity="0.35" />
            </linearGradient>
            <linearGradient id="hv-moon" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#e2e8f0" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.75" />
            </linearGradient>
            <radialGradient id="hv-glow" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#f58220" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#002d62" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="200" cy="180" r="168" fill="url(#hv-glow)" opacity="0.85" />
          <circle
            cx="200"
            cy="180"
            r="150"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="1.5"
          />
          <circle
            cx="200"
            cy="180"
            r="118"
            fill="rgba(255,255,255,0.04)"
            stroke="rgba(245,130,32,0.25)"
            strokeWidth="1.25"
          />
          <g transform="translate(108, 118)">
            <circle cx="0" cy="0" r="36" fill="url(#hv-sun)" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
              <line
                key={deg}
                x1="0"
                y1="-48"
                x2="0"
                y2="-56"
                stroke="#f58220"
                strokeWidth="3"
                strokeLinecap="round"
                transform={`rotate(${deg})`}
                opacity="0.75"
              />
            ))}
          </g>
          <g transform="translate(286, 198)">
            <circle r="34" fill="url(#hv-moon)" opacity="0.95" />
            <circle cx="13" cy="-5" r="27" fill="rgba(6, 12, 24, 0.94)" />
          </g>
          <circle cx="82" cy="92" r="2.5" fill="#fff" opacity="0.5" />
          <circle cx="312" cy="72" r="2" fill="#fff" opacity="0.4" />
          <circle cx="328" cy="108" r="1.5" fill="#fff" opacity="0.35" />
        </svg>
      </div>

      <div className="hero__visual-card">
        <img
          className="hero__visual-logo"
          src={logoSrc}
          alt=""
          width={800}
          height={240}
          decoding="async"
          fetchPriority="high"
        />
      </div>
    </div>
  );
}

const HeroVideo = memo(function HeroVideo({ videoId, title }) {
  const embedUrl = getYoutubeEmbedUrl(videoId);

  return (
    <div className="hero__visual hero__visual--video">
      <div className="hero__video-glow" aria-hidden="true" />
      <div className="hero__video-frame">
        <iframe
          className="hero__video-iframe"
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </div>
  );
});

function resolveVideoId(data) {
  if (!data) return null;
  return data.videoId || getYoutubeVideoId(data.youtubeUrl) || null;
}

function readInitialVideoId() {
  return readCachedHeroVideoId() || getYoutubeVideoId(HERO_YOUTUBE_URL) || null;
}

export default function HeroVisual() {
  const { t } = useLanguage();
  const lockedId = useRef(readInitialVideoId());
  const [videoId, setVideoId] = useState(() => lockedId.current);

  useEffect(() => {
    let cancelled = false;

    api
      .getHeroVideo()
      .then((data) => {
        if (cancelled) return;
        const id = resolveVideoId(data);
        if (id) {
          lockedId.current = id;
          writeCachedHeroVideoId(id);
          setVideoId(id);
          return;
        }
        if (data && !data.youtubeUrl && !data.videoId) {
          lockedId.current = null;
          clearCachedHeroVideoId();
          setVideoId(null);
        }
      })
      .catch(() => {
        /* keep current video — never revert to artwork after video loaded */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const activeId = videoId || lockedId.current;

  if (activeId) {
    return <HeroVideo videoId={activeId} title={t('hero.videoTitle')} />;
  }

  return <HeroArtwork />;
}
